import { FormEvent, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { clubsBookingApi } from '@/api/clubs-booking';
import { auth } from '@/store/authStore';

const ParentLoginPage = () => {
  const navigate = useNavigate();
  const [mode, setMode] = useState<'login' | 'signup'>('login');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [resendingActivation, setResendingActivation] = useState(false);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setNotice('');
    try {
      const response = mode === 'login'
        ? await clubsBookingApi.parentLogin(email, password)
        : await clubsBookingApi.parentSignup({
            name,
            email,
            password,
            confirmPassword,
          });
      auth.setAuth(response.accessToken, response.id, response.name, email, 'parent');
      clubsBookingApi.setAuthToken(response.accessToken);
      navigate('/my-bookings');
    } catch (err: any) {
      const message = err?.response?.data?.message;
      setError(Array.isArray(message) ? message.join(', ') : message || 'Parent login failed');
    } finally {
      setLoading(false);
    }
  };

  const onResendActivation = async () => {
    if (!email.trim()) {
      setError('Please enter your email first.');
      return;
    }

    setResendingActivation(true);
    setError('');
    setNotice('');
    try {
      const response = await clubsBookingApi.resendParentActivationLink(email.trim());
      setNotice(response?.message || 'If your account needs activation, a fresh link has been sent.');
    } catch (err: any) {
      const message = err?.response?.data?.message;
      setError(Array.isArray(message) ? message.join(', ') : message || 'Could not resend activation link');
    } finally {
      setResendingActivation(false);
    }
  };

  const shouldShowResendActivation = /account setup incomplete|activation link has expired|activation link is invalid/i.test(error);

  return (
    <main className="min-h-screen bg-[#f1f2f7] px-4 py-10">
      <section className="mx-auto w-full max-w-md rounded-2xl border border-slate-200 bg-white p-8 shadow-sm">
        <div className="mb-4 flex items-center gap-2">
          <img src="/logo.png" alt="Session Monkey" className="h-8 w-8 rounded-md object-contain" />
          <p className="text-lg font-semibold text-slate-900">Session Monkey</p>
        </div>
        <h1 className="text-2xl font-bold text-slate-900">{mode === 'login' ? 'Parent login' : 'Create parent account'}</h1>
        <p className="mt-2 text-sm text-slate-600">
          {mode === 'login'
            ? 'Access your child bookings and payment status.'
            : 'Create your parent account to manage bookings across clubs.'}
        </p>
        <form onSubmit={onSubmit} className="mt-6 space-y-4">
          {mode === 'signup' && (
            <label className="block">
              <span className="text-sm text-slate-700">Your name</span>
              <input
                type="text"
                className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </label>
          )}
          <label className="block">
            <span className="text-sm text-slate-700">Email</span>
            <input type="email" className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2" value={email} onChange={(e) => setEmail(e.target.value)} required />
          </label>
          <label className="block">
            <span className="text-sm text-slate-700">Password</span>
            <input type="password" className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2" value={password} onChange={(e) => setPassword(e.target.value)} required />
          </label>
          {mode === 'signup' && (
            <label className="block">
              <span className="text-sm text-slate-700">Confirm password</span>
              <input
                type="password"
                className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
              />
            </label>
          )}
          {error && <p className="text-sm text-red-600">{error}</p>}
          {notice && <p className="text-sm text-emerald-700">{notice}</p>}
          {shouldShowResendActivation && (
            <button
              type="button"
              onClick={() => void onResendActivation()}
              disabled={resendingActivation}
              className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm font-medium text-emerald-700 hover:bg-emerald-100 disabled:opacity-70"
            >
              {resendingActivation ? 'Resending...' : 'Resend setup link'}
            </button>
          )}
          <button type="submit" disabled={loading} className="w-full rounded-lg bg-[#1F8F5F] px-4 py-2 text-white hover:bg-[#1F8F5F] disabled:opacity-70">
            {loading ? 'Please wait...' : mode === 'login' ? 'Login' : 'Create account'}
          </button>
        </form>

        <div className="mt-4">
          <button
            type="button"
            onClick={() => {
              setMode(mode === 'login' ? 'signup' : 'login');
              setError('');
              setNotice('');
            }}
            className="text-sm text-slate-700 underline"
          >
            {mode === 'login' ? 'Need an account? Sign up' : 'Already have an account? Login'}
          </button>
        </div>
      </section>
    </main>
  );
};

export default ParentLoginPage;


